import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Badge, ConfirmButton, QueryBoundary, Table, Td, Th } from '../components/ui';
import { useAuth } from '../auth';
import { atLeast } from '../lib/roles';
import { dateTime } from '../lib/format';

export default function Strategies() {
  const { user } = useAuth();
  const canOperate = atLeast(user?.role, 'operator');
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ['strategies'], queryFn: () => api.strategies() });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api.setStrategyEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['strategies'] }),
  });

  return (
    <>
      <PageHeader
        title="Strategies"
        description="Strategy instances the engine will evaluate. Enable exactly one to start (docs/DEPLOYMENT.md §1.6)."
      />
      <QueryBoundary q={q} isEmpty={q.data?.items.length === 0}>
        {q.data && (
          <Table>
            <thead>
              <tr>
                <Th>Instance</Th>
                <Th>Strategy</Th>
                <Th>Symbol / TF</Th>
                <Th>Version</Th>
                <Th>State</Th>
                <Th>Changed</Th>
                {canOperate && <Th />}
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((s) => (
                <tr key={s.id ?? `${s.strategy_id}-${s.symbol}`} className="hover:bg-slate-800/30">
                  <Td>
                    {s.id != null ? (
                      <Link className="text-sky-400 hover:underline" to={`/strategies/${s.id}`}>
                        #{s.id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td>{s.strategy_id}</Td>
                  <Td>
                    {s.symbol} · {s.timeframe}
                  </Td>
                  <Td>{s.strategy_version}</Td>
                  <Td>
                    {s.enabled ? <Badge tone="good">enabled</Badge> : <Badge>disabled</Badge>}
                  </Td>
                  <Td className="text-xs text-slate-500">
                    {s.enabled ? dateTime(s.enabled_at) : dateTime(s.disabled_at)}
                  </Td>
                  {canOperate && s.id != null && (
                    <Td>
                      <ConfirmButton
                        title={`${s.enabled ? 'Disable' : 'Enable'} ${s.strategy_id}?`}
                        body={
                          s.enabled
                            ? 'The engine will stop generating signals for this instance.'
                            : `The engine will start evaluating ${s.strategy_id} on ${s.symbol} ${s.timeframe}.`
                        }
                        confirmLabel={s.enabled ? 'Disable' : 'Enable'}
                        variant={s.enabled ? 'danger' : 'primary'}
                        onConfirm={() => toggle.mutateAsync({ id: s.id as number, enabled: !s.enabled })}
                      >
                        {s.enabled ? 'Disable' : 'Enable'}
                      </ConfirmButton>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </QueryBoundary>
      {!canOperate && (
        <p className="mt-3 text-xs text-slate-500">Enable / disable requires the operator role.</p>
      )}
    </>
  );
}
